import { gql, useQuery } from '@apollo/client'
import { item as itemCommons, ITEM_STATUS, itemState, money, freightUtils } from '@sellpy/commons'
import config from 'config'
import { List } from 'immutable'
import { DateTime, Duration } from 'luxon'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getItemValue } from '../../common/items/actions'
import { locale, localizedMetadata, getRegion } from '../../common/region/config'
import cache from '../apollo/cache.js'
import { zeroMoney } from '../payOuts/utils'
import { useCurrentUser } from '../../common/user/currentApolloUser'
import { useAlgoliaItem } from '../../common/items/algoliaItems'
import { itemStateMap } from './itemStatus'
import { skipFourthImageItemTypeIds, skipThirdImageItemTypeIds } from './skipImageItemTypeIds'

const { staticImageAssets } = config

export const useAlgoliaPrice = ({ itemId }) => {
  const { item: algoliaItem } = useAlgoliaItem({ itemId })

  const subUnitPrice = algoliaItem?.[`price_${getRegion()}`]
  const priceObject = subUnitPrice && money.toBaseUnit(subUnitPrice)

  return priceObject || zeroMoney()
}

export const useSellerItemValue = (itemId) => {
  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(getItemValue(itemId))
  }, [dispatch, itemId])
  const itemValues = useSelector((state) => state.items.itemValues)
  return itemValues.get(itemId)
}

const SELLER_ITEM_VALUE_QUERY = gql`
  query getSellerItemValue($itemId: ID!) {
    getItemValues(itemIds: [$itemId])
  }
`
export const useSellerItemValueGql = (itemId) => {
  const { data, loading } = useQuery(SELLER_ITEM_VALUE_QUERY, {
    variables: {
      itemId
    }
  })
  if (loading) return null
  return data?.getItemValues && data.getItemValues[itemId]
}

const GET_IS_FIRST_TIME_USER = gql`
  query getIsFirstTimeMarketCustomer {
    isFirstTimeMarketCustomer
  }
`

export const useIsFirstTimeMarketCustomer = () => {
  const user = useCurrentUser()
  const { data: { isFirstTimeMarketCustomer } = {} } = useQuery(GET_IS_FIRST_TIME_USER, {
    skip: !user
  })
  return !user || isFirstTimeMarketCustomer
}

export const evictItemValueCache = (itemId) => {
  cache.evict({
    id: 'ROOT_QUERY',
    fieldName: 'getItemValues',
    args: {
      itemIds: [itemId]
    }
  })
  cache.gc()
}

export const evictItemLastPricingPeriodCache = (itemId) => {
  cache.evict({
    id: 'ROOT_QUERY',
    fieldName: 'getLastPricingPeriodForItem',
    args: {
      itemId: itemId
    }
  })
  cache.gc()
}

export const evictItemCacheForChangeRequestForm = (itemId) => {
  cache.evict({
    id: `Item:${itemId}`,
    fieldName: 'itemStatus'
  })

  cache.evict({
    id: `ROOT_QUERY`,
    fieldName: 'fetchItemChangeRequests',
    args: {
      itemId: itemId
    }
  })

  cache.gc()
}

export const evictItemCacheForReevaluationForm = (itemId) => {
  cache.evict({
    id: `Item:${itemId}`,
    fieldName: 'itemStatus'
  })
  cache.evict({
    fieldName: 'getDonateAfter'
  })
  cache.evict({
    fieldName: 'getSendBackRequestForItem'
  })
  cache.evict({
    fieldName: 'getSaleRequestForItem'
  })
  cache.evict({
    fieldName: 'ifRejectedDonateAfter'
  })

  cache.gc()
}

export const isOwnerOfItem = (item, userId) => item && userId && item.get('user') === userId

export const EMPTY_METADATA_ALLOWED_SINCE = new Date('2020-01-01')

export const isViewable = ({ item, isOwner }) => {
  if (!item) return false
  const metadata = item[localizedMetadata]
  const hasType = Boolean(metadata?.type)
  const itemStatus = item.itemStatus
  const _forSeller = new Date(item.createdAt) > EMPTY_METADATA_ALLOWED_SINCE
  const _forBuyer =
    hasType &&
    itemState.sellable({ itemStatus }) &&
    !itemState.pendingSale({ itemStatus }) &&
    !itemState.removed({ itemStatus })

  return isOwner ? _forSeller : _forBuyer
}

const defaultPhoto = (itemStatus) => {
  return { url: defaultImage(itemStatus) }
}

const defaultImage = (itemStatus) =>
  ({
    donated: `${staticImageAssets}sellerInterface/saleItems/april2021/donate-to-charity.png`,
    pendingReevaluation: `${staticImageAssets}sellerInterface/saleItems/april2021/sale-request.png`,
    sellerReturned: `${staticImageAssets}sellerInterface/saleItems/april2021/sent-back-to-seller.jpg`,
    rejected: `${staticImageAssets}sellerInterface/saleItems/april2021/rejected-items.jpg`
  }[itemStateMap[itemStatus]] ||
  `${staticImageAssets}sellerInterface/saleItems/april2021/in-progress.png`)

export const defaultSellerPhoto = (itemStatus) => {
  return { url: defaultSellerImage(itemStatus) }
}

const defaultSellerImage = (itemStatus) => {
  switch (itemStatus) {
    case ITEM_STATUS.UTSORTERAD:
      return `${staticImageAssets}sellerInterface/saleItems/october24/sortedForRecycling.png`

    case ITEM_STATUS.TILLBAKASKICKAD_DIREKT:
      return `${staticImageAssets}sellerInterface/saleItems/october24/returned.png`

    case ITEM_STATUS.OMBEDÖMNING_BEGÄRD:
      return `${staticImageAssets}sellerInterface/saleItems/october24/reevaluation.png`

    case ITEM_STATUS.REGISTRERAD:
    case ITEM_STATUS.VILANDE:
      return `${staticImageAssets}sellerInterface/saleItems/october24/bagProcessing.png`

    case ITEM_STATUS.SKÄNKT:
      return `${staticImageAssets}sellerInterface/saleItems/october24/donated.png`

    case ITEM_STATUS.SKÄNKT_DIREKT:
      return `${staticImageAssets}sellerInterface/saleItems/october24/recycle.png`

    default:
      return `${staticImageAssets}sellerInterface/saleItems/october24/fallback.png`
  }
}

export const calculateShareAboveTreshold = ({
  expensiveItemShareFactor,
  value,
  expensiveItemShareThreshold
}) => {
  if (money.lte(value, expensiveItemShareThreshold)) return { amount: 0, currency: value.currency }

  return money.multiply(expensiveItemShareFactor)(
    money.subtract(value)(expensiveItemShareThreshold)
  )
}

export const calculateShareBelowThreshold = ({
  value,
  shareFactor,
  expensiveItemShareThreshold
}) => {
  if (money.lte(expensiveItemShareThreshold, value))
    return money.multiply(shareFactor)(expensiveItemShareThreshold)
  return money.multiply(shareFactor)(value)
}

export const calculateSellerShare =
  ({ expensiveItemShareThreshold, expensiveItemShareFactor, adFee, shareFactor }) =>
  ({ value }) => {
    if (!expensiveItemShareThreshold) return money.multiply(shareFactor)(value)
    if (money.lte(value, expensiveItemShareThreshold))
      return money.subtract(money.multiply(shareFactor)(value))(adFee)

    const shareAboveTreshold = calculateShareAboveTreshold({
      expensiveItemShareFactor,
      value,
      expensiveItemShareThreshold
    })
    const shareBelowThreshold = calculateShareBelowThreshold({
      value,
      shareFactor,
      expensiveItemShareThreshold
    })
    return money.subtract(money.add(shareAboveTreshold, shareBelowThreshold))(adFee)
  }

const FLIXSTOCK_DEWRINKLE_IMAGE_PATH = 'flixstock-images/dewrinkle/'

export const selectPhotosForUse = ({ itemStatus, photos }) => {
  return selectPhotosByItemStatus({ itemStatus, photos }).filter(
    (photo) => !photo.url?.includes(FLIXSTOCK_DEWRINKLE_IMAGE_PATH)
  )
}

const discardMLInAssortment = (photo) => photo.type !== itemCommons.PHOTO_TYPES.type.type
const discardPriceTag = (photo) => photo.type !== itemCommons.PHOTO_TYPES.priceTag.type

export const orderPhotosIfMLPhotos = ({ photos }) => {
  return [...photos]?.sort((a, b) => {
    if (a.value.type === itemCommons.PHOTO_TYPES.type.type) return -1
    if (b.value.type === itemCommons.PHOTO_TYPES.type.type) return 1
    return 0
  })
}

export const orderRobotPhotosFirst = (photos) => {
  const robotPhotos = photos.filter((photo) => photo.value.type === 'robot')
  const otherPhotos = photos.filter((photo) => photo.value.type !== 'robot')
  return [...robotPhotos, ...otherPhotos]
}

export const selectPhotosForUseSorted = ({ itemStatus, photos }) => {
  return selectPhotosByItemStatus({
    itemStatus,
    photos: photos.filter(discardMLInAssortment).filter(discardPriceTag)
  }).sort((a, b) => Number(a.type === 'defect') - Number(b.type === 'defect'))
}

export const selectPhotosForUseSortedWithSkipping = ({ itemStatus, photos, itemTypeId }) => {
  let sortedPhotos = selectPhotosByItemStatus({
    itemStatus,
    photos: photos.filter(discardMLInAssortment).filter(discardPriceTag)
  }).sort((a, b) => Number(a.type === 'defect') - Number(b.type === 'defect'))
  if (skipFourthImageItemTypeIds.includes(itemTypeId))
    sortedPhotos = sortedPhotos.filter((_, index) => index !== 3)
  if (skipThirdImageItemTypeIds.includes(itemTypeId))
    sortedPhotos = sortedPhotos.filter((_, index) => index !== 2)
  return sortedPhotos
}

export const selectPhotosForUseSeller = ({ photos, itemStatus }) => {
  const withoutDeWrinkle = photos.filter(
    (photo) => !photo.url?.includes(FLIXSTOCK_DEWRINKLE_IMAGE_PATH)
  )

  if (
    [
      ITEM_STATUS.TILLBAKASKICKAD_DIREKT,
      ITEM_STATUS.UTSORTERAD,
      ITEM_STATUS.REGISTRERAD,
      ITEM_STATUS.OMBEDÖMNING_BEGÄRD,
      ITEM_STATUS.SKÄNKT_DIREKT
    ].includes(itemStatus)
  )
    return withoutDeWrinkle

  return withoutDeWrinkle.filter(discardMLInAssortment)
}

export const selectImagesForUse = (images) => {
  return images.filter((url) => !url?.includes(FLIXSTOCK_DEWRINKLE_IMAGE_PATH))
}

const selectPhotosByItemStatus = ({ photos, itemStatus }) => {
  return (
    photos?.map((photo) => photo.value)?.filter(discardMLInAssortment) || [defaultPhoto(itemStatus)]
  )
}

export const selectImagesByItemStatus = (item) => {
  if (item.itemStatus === ITEM_STATUS.REGISTRERAD) return [defaultImage(ITEM_STATUS.REGISTRERAD)]
  return item.images || [defaultImage(item.itemStatus)]
}

/**
 * @deprecated use selectImagesByItemStatus instead
 */
export const selectImagesByItemStatusImmutable = (item) => {
  if (item.get('itemStatus') === ITEM_STATUS.REGISTRERAD)
    return List([defaultImage(ITEM_STATUS.REGISTRERAD)])
  return item.get('images') || List([defaultImage(item.get('itemStatus'))])
}

export const getFastestFreightAlternative = (freightAlternatives) => {
  const alternativesWithoutExpressDelivery = freightAlternatives.filter(
    (alternative) => !alternative.isExpressDelivery
  )
  return freightUtils.getFastestAlternative(alternativesWithoutExpressDelivery)
}

export const getIntervalDatesForFreightAlternative = (freightAlternative) => {
  const [min, max] = freightAlternative.estimatedShipping.interval.split('/')
  return {
    earliest: DateTime.local().plus(Duration.fromISO(min)),
    latest: DateTime.local().plus(Duration.fromISO(max))
  }
}

export const getDeliveryDisplay = (freightAlternative, format) => {
  const [min, max] = freightAlternative.estimatedShipping.interval.split('/')

  const expressDate = freightAlternative.isExpressDelivery
    ? freightAlternative.estimatedShipping.minDeliverBefore
    : undefined

  return freightUtils.getDeliveryDisplayDate({
    expressDate,
    min,
    max,
    locale,
    region: getRegion(),
    format
  })
}

export const evictUserBalanceCache = (currency) => {
  cache.evict({
    id: 'ROOT_QUERY',
    fieldName: 'getTotalBalanceForUser',
    args: {
      currency: currency
    }
  })
  cache.gc()
}
export const evictPayoutsCache = () => {
  cache.evict({
    id: 'ROOT_QUERY',
    fieldName: 'payOuts'
  })
  cache.gc()
}

export const evictAutomaticPayoutConfigCache = () => {
  cache.evict({
    id: 'ROOT_QUERY',
    fieldName: 'automaticPayoutConfigs'
  })
  cache.gc()
}
